import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { isValidExamCode, normalizeExamCode, type ExamCodeLookup } from 'shared';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';
import { translateApiError } from '@/lib/api-error';

/**
 * หน้าแรกของโดเมน — คนที่มาถึงนี่คือคนที่ไม่ได้กดลิงก์จากผู้จัดอบรม
 * (สแกน QR ไม่ได้ / จำได้แต่ชื่อเว็บ) จึงต้องมีทางเข้าด้วยรหัสสั้น ไม่ใช่หน้าสถานะระบบ
 */
export function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  const lookup = useMutation({
    mutationFn: (value: string) =>
      apiFetch<ExamCodeLookup>(`/public/exam-code/${encodeURIComponent(value)}`),
    onSuccess: ({ accessToken }) => {
      // ไปหน้า landing ปกติ — สถานะเปิด/ปิดและคำชี้แจงอยู่ที่นั่นอยู่แล้ว
      void navigate(`/t/${accessToken}`);
    },
  });

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const normalized = normalizeExamCode(code);
    if (!isValidExamCode(normalized)) {
      setFieldError(t('home.codeInvalid'));
      return;
    }
    setFieldError(null);
    lookup.mutate(normalized);
  };

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-xl flex-col gap-6 px-5 py-10">
      <header className="flex items-start justify-between gap-4">
        <div>
          {/* หน้าแรกใช้ชื่อสั้น — ชื่อเต็มกับชื่อหน่วยงานอยู่ที่หน้าอื่นแล้ว */}
          <h1 className="text-2xl font-semibold text-slate-900">{t('home.title')}</h1>
          <p className="mt-1 text-sm text-slate-600">{t('app.subtitle')}</p>
        </div>
        <LanguageSwitcher />
      </header>

      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900">{t('home.codeTitle')}</h2>
        <p className="mt-1 text-sm text-slate-600">{t('home.codeHint')}</p>

        <form className="mt-4 flex flex-col gap-2 sm:flex-row" onSubmit={onSubmit} noValidate>
          <div className="flex-1">
            <label htmlFor="examCode" className="sr-only">
              {t('home.codeLabel')}
            </label>
            <input
              id="examCode"
              name="examCode"
              value={code}
              // พิมพ์เล็ก/ใส่ขีดได้ ระบบ normalize ให้เอง แต่แสดงเป็นตัวใหญ่ให้ตรงกับที่พิมพ์บนกระดาษ
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              maxLength={24}
              placeholder={t('home.codePlaceholder')}
              aria-invalid={Boolean(fieldError)}
              aria-describedby={fieldError ? 'examCode-error' : undefined}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 font-mono text-lg tracking-[0.2em] uppercase"
            />
          </div>
          <Button type="submit" size="lg" disabled={lookup.isPending}>
            {lookup.isPending ? t('common.loading') : t('home.codeSubmit')}
            <ArrowRight aria-hidden className="h-4 w-4" />
          </Button>
        </form>

        {fieldError && (
          <p id="examCode-error" className="mt-2 text-sm text-red-700">
            {fieldError}
          </p>
        )}
        {lookup.isError && (
          <p className="mt-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {translateApiError(lookup.error, t)}
          </p>
        )}
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-base font-semibold text-slate-900">{t('home.helpTitle')}</h2>
        <p className="mt-1 text-sm text-slate-600">{t('home.helpBody')}</p>
      </section>

      <footer className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-600">
        <Link to="/privacy" className="underline-offset-2 hover:underline">
          {t('home.privacy')}
        </Link>
        <Link to="/admin/login" className="underline-offset-2 hover:underline">
          {t('home.adminLogin')}
        </Link>
      </footer>
    </main>
  );
}
